import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Search, Play, Pause, User, Trash2 } from 'lucide-react';
import { EmptyState, LoadingSpinner } from './shared';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: 'all', label: 'Alla användare' },
  { value: 'customer', label: 'Kunder' },
  { value: 'store_owner', label: 'Butiksägare' },
  { value: 'admin', label: 'Administratörer' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alla' },
  { value: 'active', label: 'Aktiva' },
  { value: 'paused', label: 'Pausade' },
];

const formatSEK = (n) => (n || 0).toLocaleString('sv-SE') + ' kr';
const formatDate = (d) => d ? new Date(d).toLocaleDateString('sv-SE') : '-';

const MiniStat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3" data-testid={`customer-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-9 h-9 rounded-xl ${color} flex items-center justify-center`}>
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div>
      <p className="text-xl font-black text-[#0F172A]">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);

const RoleBadge = ({ role }) => {
  const map = {
    store_owner: { label: 'Butiksägare', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    admin: { label: 'Admin', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
    customer: { label: 'Kund', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
  };
  const c = map[role] || map.customer;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${c.cls}`} data-testid={`role-badge-${role}`}>{c.label}</span>;
};

export const CustomersSection = ({ customers, loading, search, setSearch, onSearch, onTogglePause, onDeleteUser }) => {
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(search, roleFilter, statusFilter);
  };

  const handleFilterChange = (role, status) => {
    setRoleFilter(role);
    setStatusFilter(status);
    onSearch(search, role, status);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="admin-customers">
      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Sök kundnummer, namn eller e-post..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 rounded-xl"
              data-testid="customer-search-input"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => handleFilterChange(e.target.value, statusFilter)}
              className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#003B73]/20"
              data-testid="customer-role-filter"
            >
              {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange(roleFilter, e.target.value)}
              className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#003B73]/20"
              data-testid="customer-status-filter"
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Button type="submit" className="bg-[#003B73] hover:bg-[#002a52] rounded-xl h-10 px-5" data-testid="customer-search-btn">
              <Search className="h-4 w-4 mr-2" />Sök
            </Button>
          </div>
        </form>
      </div>

      {/* Results Table */}
      {customers.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-[#0F172A]">Användare ({customers.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="text-left p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Användare</th>
                  <th className="text-left p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden md:table-cell">Roll</th>
                  <th className="text-left p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden lg:table-cell">Kundnr</th>
                  <th className="text-right p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden md:table-cell">Ordrar</th>
                  <th className="text-right p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden lg:table-cell">Totalt köpt</th>
                  <th className="text-left p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider hidden xl:table-cell">Registrerad</th>
                  <th className="text-right p-4 font-semibold text-gray-500 text-xs uppercase tracking-wider">Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors ${customer.is_paused ? 'bg-red-50/40' : ''}`}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${customer.is_paused ? 'bg-red-100' : 'bg-gray-100'}`}>
                          <User className={`h-4 w-4 ${customer.is_paused ? 'text-red-500' : 'text-gray-500'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate flex items-center gap-2">
                            {customer.name || 'Namnlös'}
                            {customer.is_paused && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">PAUSAD</span>}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{customer.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <RoleBadge role={customer.role} />
                      {customer.store && (
                        <p className="text-[10px] text-gray-400 mt-1 truncate max-w-[120px]">{customer.store.name}</p>
                      )}
                    </td>
                    <td className="p-4 hidden lg:table-cell">
                      <span className="font-mono text-xs font-semibold text-[#003B73] bg-blue-50 px-2 py-1 rounded">
                        {customer.customer_number || '-'}
                      </span>
                    </td>
                    <td className="p-4 text-right hidden md:table-cell">
                      <span className="font-semibold text-gray-700">{customer.order_count || 0}</span>
                    </td>
                    <td className="p-4 text-right hidden lg:table-cell">
                      <span className="font-semibold text-gray-700">{formatSEK(customer.total_spent)}</span>
                    </td>
                    <td className="p-4 hidden xl:table-cell">
                      <span className="text-xs text-gray-500">{formatDate(customer.created_at)}</span>
                    </td>
                    <td className="p-4 text-right">
                      {customer.role !== 'admin' && (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant={customer.is_paused ? 'default' : 'outline'}
                            className={`h-8 rounded-lg ${customer.is_paused ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                            onClick={() => onTogglePause(customer.id)}
                            title={customer.is_paused ? 'Aktivera konto' : 'Pausa konto'}
                            data-testid={`toggle-pause-${customer.id}`}
                          >
                            {customer.is_paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-lg border-red-200 text-red-500 hover:bg-red-50"
                            onClick={() => setDeleteTarget(customer)}
                            title="Ta bort användare"
                            data-testid={`delete-user-${customer.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <EmptyState message="Inga användare hittades. Prova att söka med namn, e-post eller kundnummer." />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Ta bort användare</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Är du säker på att du vill ta bort denna kund?
            </p>
            {deleteTarget && (
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="font-semibold text-gray-900">{deleteTarget.name || 'Namnlös'}</p>
                <p className="text-sm text-gray-500">{deleteTarget.email}</p>
              </div>
            )}
            <p className="text-xs text-red-500">Detta kan inte ångras. Alla uppgifter raderas permanent.</p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline" className="rounded-xl">Avbryt</Button></DialogClose>
            <Button
              className="bg-red-600 hover:bg-red-700 rounded-xl"
              onClick={() => { onDeleteUser(deleteTarget.id); setDeleteTarget(null); }}
              data-testid="confirm-delete-user-btn"
            >
              Ta bort
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
